<template>
  <div class="container">
    <!-- Success and Error Messages -->
    <div v-if="successMessage" class="alert alert-success alert-dismissible fade show fixed-alert" role="alert">
      {{ successMessage }}
      <button type="button" class="btn-close" @click="successMessage = ''"></button>
    </div>

    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show fixed-alert" role="alert">
      {{ errorMessage }}
      <button type="button" class="btn-close" @click="errorMessage = ''"></button>
    </div>

    <!-- Page Title -->
    <div class="mt-4">
      <h2>{{ $t('groupsAndPermissions') }}</h2>
    </div>

    <!-- Add Group Button -->
    <div class="mb-4">
      <button v-if="hasPermission('auth.add_group')" class="btn btn-primary"
              @click="$router.push({ name: 'GroupAdd' })">
        <i class="bi bi-plus-circle"></i> {{ $t('add_group') }}
      </button>
    </div>

    <!-- Group List -->
    <div class="columns is-multiline">
      <div class="column is-12">
        <template v-if="groups.length">
          <table class="table table-hover">
            <thead>
            <tr>
              <th>ID</th>
              <th>{{ $t('group_name') }}</th>
              <th>{{ $t('permissions') }}</th>
              <th>{{ $t('users') }}</th>
              <th>{{ $t('actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="group in groups" :key="group.id">
              <td>{{ group.id }}</td>
              <td>{{ group.name }}</td>
              <td>
                <ul>
                  <li v-for="(permission, index) in group.permissions.slice(0, 3)" :key="permission.id">
                    {{ permission.name }}
                  </li>
                  <li style="list-style: none" v-if="group.permissions.length > 3">
                    <button @click="toggleShowMore(group.id)" class="btn btn-link p-0">
                      <span v-if="!showMore[group.id]">{{ $t('show_more') }}</span>
                      <span v-if="showMore[group.id]">{{ $t('show_less') }}</span>
                    </button>
                  </li>
                  <li v-if="showMore[group.id]" v-for="(permission, index) in group.permissions.slice(3)"
                      :key="permission.id">
                    {{ permission.name }}
                  </li>
                </ul>
              </td>
              <td>
                <ul>
                  <!-- First 3 users -->
                  <li v-for="userId in group.user_ids.slice(0, 3)" :key="userId">
                    <span>{{ getUserById(userId)?.username || $t('unknown') }}</span>
                  </li>

                  <!-- Toggle button if more than 3 -->
                  <li style="list-style: none" v-if="group.user_ids.length > 3">
                    <button @click="toggleShowMoreUsers(group.id)" class="btn btn-link p-0">
                      <span v-if="!showMoreUsers[group.id]">{{ $t('show_more') }}</span>
                      <span v-else>{{ $t('show_less') }}</span>
                    </button>
                  </li>

                  <!-- Remaining users -->
                  <li
                      v-if="showMoreUsers[group.id]"
                      v-for="userId in group.user_ids.slice(3)"
                      :key="userId"
                  >
                    <span>{{ getUserById(userId)?.username || $t('unknown') }}</span>
                  </li>
                </ul>
              </td>

              <td>
                <button v-if="hasPermission('auth.change_group')" class="btn btn-primary btn-sm"
                        @click="goToEditGroup(group.id)">
                  <i class="bi bi-pencil-square"></i> {{ $t('edit') }}
                </button>
                <button v-if="hasPermission('auth.delete_group')" @click="openConfirmDelete(group.id)"
                        class="btn btn-danger btn-sm">
                  <i class="bi bi-trash"></i> {{ $t('delete') }}
                </button>
                <button v-if="hasPermission('auth.change_group')" class="btn btn-secondary btn-sm"
                        style="margin-left: 10px" @click="goToManageUsers(group.id)">
                  <i class="bi bi-people"></i> {{ $t('manage_users') }}
                </button>
              </td>
            </tr>
            </tbody>
          </table>

          <!-- Pagination Buttons -->
          <div class="buttons">
            <button class="btn btn-light" @click="goToPreviousPage" v-if="showPreviousButton">{{
                $t('previous')
              }}
            </button>
            <button class="btn btn-light" @click="goToNextPage" v-if="showNextButton">{{ $t('next') }}</button>
          </div>
        </template>

        <!-- No groups available -->
        <div v-else>
          <p>{{ $t('no_groups_found') }}</p>
        </div>
      </div>
    </div>

    <!-- Custom Confirm Delete Modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="modal-container">
        <h3 class="modal-title">{{ $t('confirm_delete') }}</h3>
        <p>{{ $t('delete_group_confirmation') }}</p>
        <div class="modal-actions">
          <button class="btn btn-danger" @click="deleteGroup">{{ $t('yes_delete') }}</button>
          <button class="btn btn-primary" @click="cancelDelete">{{ $t('cancel') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import { useMainStore } from '@/store';
import axios from 'axios';

export default {
  name: 'GroupList',
  data() {
    return {
      groups: [],
      users: [],
      showMore: {},
      showMoreUsers: {},
      showNextButton: false,
      showPreviousButton: false,
      currentPage: 1,
      successMessage: '',
      errorMessage: '',
      showConfirm: false,
      groupIdToDelete: null
    };
  },
  mounted() {
    this.getGroups();
    this.getUsers();
  },
  computed: {
    store: () => useMainStore(),
    hasPermission() {
      return (permission) => {
        const userPermissions = this.store.user.permissions || [];
        return userPermissions.includes(permission);
      };
    }
  },

  methods: {
    getUserById(id) {
      const intId = Number(id);
      return this.users.find(user => Number(user.id) === intId);
    },
    toggleShowMore(groupId) {
      if (this.showMore[groupId] === undefined) {
        this.showMore[groupId] = true;
      } else {
        this.showMore[groupId] = !this.showMore[groupId];
      }
    },
    toggleShowMoreUsers(groupId) {
      if (this.showMoreUsers[groupId] === undefined) {
        this.showMoreUsers[groupId] = true;
      } else {
        this.showMoreUsers[groupId] = !this.showMoreUsers[groupId];
      }
    },
    openConfirmDelete(groupId) {
      this.groupIdToDelete = groupId;
      this.showConfirm = true;
    },
    async deleteGroup() {
      try {
        await axios.delete(`/api/v1/admin/groups/${this.groupIdToDelete}/`);
        this.successMessage = 'Group deleted successfully';
        this.getGroups();
        setTimeout(() => {
          this.successMessage = '';
        }, 2000);
      } catch (error) {
        this.errorMessage = 'Failed to delete group';
        setTimeout(() => {
          this.errorMessage = '';
        }, 2000);
      }
      this.showConfirm = false;
    },
    goToEditGroup(groupId) {
      this.$router.push({name: 'GroupEdit', params: {id: groupId}});
    },
    goToManageUsers(groupId) {
      this.$router.push({name: 'GroupManageUsers', params: {id: groupId}});
    },
    cancelDelete() {
      this.showConfirm = false;
    },
    async getGroups() {
      this.showNextButton = false;
      this.showPreviousButton = false;
      try {
        const response = await axios.get(`/api/v1/admin/groups/?page=${this.currentPage}`);
        this.groups = response.data.results;

        if (response.data.next) {
          this.showNextButton = true;
        }
        if (response.data.previous) {
          this.showPreviousButton = true;
        }
      } catch (error) {
        this.errorMessage = 'Failed to fetch groups';
        setTimeout(() => {
          this.errorMessage = '';
        }, 2000);
      }
    },
    async getUsers() {
      try {
        let url = '/api/v1/admin/users/?page=1';
        const allUsers = [];

        while (url) {
          const response = await axios.get(url);
          const data = response.data;

          allUsers.push(...(data.results || []));

          // Convert absolute next link to relative (prevents http/https mismatch)
          url = data.next ? new URL(data.next).pathname + new URL(data.next).search : null;
        }

        this.users = allUsers;
      } catch (error) {
        this.errorMessage = 'Failed to fetch users';
        setTimeout(() => (this.errorMessage = ''), 2000);
      }
    },
    goToNextPage() {
      this.currentPage += 1;
      this.getGroups();
    },
    goToPreviousPage() {
      this.currentPage -= 1;
      this.getGroups();
    }
  }
};
</script>
